function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
      
      // Handle clicks on swatch labels to ensure change event is triggered
      facetForm.addEventListener('click', (event) => {
        const label = event.target.closest('.swatch-input__label');
        if (label && label.hasAttribute('for')) {
          const input = document.getElementById(label.getAttribute('for'));
          if (input && input.type === 'checkbox' && !input.disabled) {
            // Let the browser handle the click first, then trigger change if needed
            requestAnimationFrame(() => {
              input.dispatchEvent(new Event('change', { bubbles: true }));
            });
          }
        }
      });
    }

    const facetWrapper = this.querySelector('[id^="FacetsWrapper-"]');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      productGridContainer.classList.add('loading');
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceContainer = parsedHTML.getElementById('ProductGridContainer');
    if (!sourceContainer) return;

    productGridContainer.innerHTML = sourceContainer.innerHTML;

    productGridContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    
    if (sourceCount && container) {
      container.innerHTML = sourceCount.innerHTML;
      container.classList.remove('loading');
    }
    
    if (sourceCount && containerDesktop) {
      containerDesktop.innerHTML = sourceCount.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '[id^="FacetFiltersForm"] .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '[id^="FacetFiltersForm"] .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentId = elementToRender.parentElement.id;
          const parentInDom = document.getElementById(parentId);
          if (parentInDom) {
            const firstFilter = parentInDom.querySelector('.js-filter');
            if (firstFilter) {
              firstFilter.before(elementToRender);
            } else {
              parentInDom.appendChild(elementToRender);
            }
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event) {
      const closestJSFilter = event.target.closest('.js-filter');
      if (!closestJSFilter?.id) return;

      const target = event.target;
      const inputType = target.getAttribute('type');
      const isText = inputType === 'text';
      const isCheckable = inputType === 'checkbox' || inputType === 'radio';
      const targetId = target.id;
      const targetName = target.getAttribute('name');
      const targetValue = target.value;
      const selStart = isText ? target.selectionStart : null;
      const selEnd = isText ? target.selectionEnd : null;

      FacetFiltersForm.renderCounts(countsToRender, closestJSFilter);

      const newFilter = document.getElementById(closestJSFilter.id);
      if (!newFilter) return;

      if (newFilter.matches('details.facets__disclosure[tabindex="-1"]')) {
        newFilter.removeAttribute('tabindex');
      }

      if (!isText && !isCheckable && newFilter.matches('details.facets__disclosure')) {
        (newFilter.querySelector('summary') || newFilter).focus({ preventScroll: true });
        return;
      }

      let toFocus = null;
      if (isCheckable || isText) {
        if (targetId) {
          const byId = document.getElementById(targetId);
          if (byId && newFilter.contains(byId)) toFocus = byId;
        }
        if (!toFocus && targetName) {
          toFocus = isCheckable
            ? [...newFilter.querySelectorAll('input[type="checkbox"], input[type="radio"]')]
                .find((el) => el.name === targetName && el.value === targetValue)
            : newFilter.querySelector(`input[type="text"][name="${targetName}"]`);
        }
        if (!toFocus && isCheckable && newFilter.classList.contains('filter-availability')) {
          toFocus = newFilter.querySelector('input[type="checkbox"]:not([disabled])');
        }
      }

      if (!toFocus) return;

      requestAnimationFrame(() => {
        if (isText) {
          const active = document.activeElement;
          if (
            active instanceof HTMLElement &&
            active !== document.body &&
            active !== document.documentElement &&
            active !== toFocus &&
            active.matches('input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')
          ) return;
        }

        toFocus.focus({ preventScroll: true });
        if (isText && toFocus instanceof HTMLInputElement && selStart !== null) {
          const len = toFocus.value.length;
          toFocus.setSelectionRange(Math.min(selStart, len), Math.min(selEnd, len));
        }
      });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const elementSelectors = ['.sorting'];

    elementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = html.querySelector(selector).innerHTML;
      }
    });
  }

  static renderCounts(source, target) {
    // Update summary if it exists (for details elements)
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    // Update header if it exists (for price range)
    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    // Update wrap element (contains the actual filter items)
    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      // Preserve show-more state
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }


  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('ProductGridContainer');
    if (!productGrid) return [];
    
    return [
      {
        section: productGrid.dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    if (searchParams === FacetFiltersForm.searchParamsPrev) {
      return;
    }
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];
    const formElement = event.target.closest('form');
    const isNewForm = formElement && formElement.id.startsWith('FacetFiltersForm-');

    sortFilterForms.forEach((form) => {
      if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm' || isNewForm) {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
    this.controlSlider();
    this.controlInput();
    this.fillSlider();
  }

  onRangeChange(event) {
    const input = event.currentTarget;
    const oldValue = input.value;
    
    this.adjustToValidValues(input);
    this.setMinAndMaxValues();
    
    // If value was adjusted, sync with range sliders
    if (oldValue !== input.value || input.classList.contains('field__input')) {
      this.syncInputWithSlider(input);
      this.fillSlider();
    }
  }
  
  syncInputWithSlider(input) {
    const inputRangeWrappers = this.querySelectorAll(".facets__range");
    const inputNumberWrappers = this.querySelectorAll(".facets__price-wrapper");
    
    inputNumberWrappers.forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll(".field__input");
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom = inputRangeWrappers[index]?.querySelectorAll(".field__range")[0];
      const inputRangeTo = inputRangeWrappers[index]?.querySelectorAll(".field__range")[1];
      
      if (!inputRangeFrom || !inputRangeTo) return;
      
      if (input === inputNumberFrom) {
        const value = parseFloat(inputNumberFrom.value) || 0;
        inputRangeFrom.value = value;
      } else if (input === inputNumberTo) {
        const value = parseFloat(inputNumberTo.value) || 0;
        inputRangeTo.value = value;
      }
    });
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('.field__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!minInput || !maxInput) return;

    if (maxInput.value) {
      minInput.setAttribute('data-max', maxInput.value);
    }
    if (minInput.value) {
      maxInput.setAttribute('data-min', minInput.value);
    }
    if (minInput.value === '') {
      maxInput.setAttribute('data-min', '0');
    }
    if (maxInput.value === '') {
      const originalMax = maxInput.getAttribute('data-max');
      if (originalMax) {
        minInput.setAttribute('data-max', originalMax);
      }
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min')) || 0;
    const max = Number(input.getAttribute('data-max')) || Number.MAX_SAFE_INTEGER;

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  fillSlider() {
    this.querySelectorAll(".facets__range").forEach(inputWrapper => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      
      if (!inputRangeFrom || !inputRangeTo) return;
      
      const range = inputRangeTo.max - inputRangeTo.min;
      const fromCurrent = inputRangeFrom.value - inputRangeTo.min;
      const toCurrent = inputRangeTo.value - inputRangeTo.min;
      const minRange = (fromCurrent / range) * 100;
      const maxRange = (toCurrent / range) * 100;

      inputWrapper.setAttribute("style", `--range-min: ${minRange}%; --range-max: ${maxRange}%`);
    });
  }

  controlSlider() {
    const inputRangeWrappers = this.querySelectorAll(".facets__range");
    const inputNumberWrappers = this.querySelectorAll(".facets__price-wrapper");

    inputRangeWrappers.forEach((inputWrapper, index) => {
      const inputsRange = inputWrapper.querySelectorAll(".field__range");
      const inputRangeFrom = inputsRange[0];
      const inputRangeTo = inputsRange[1];
      const inputNumberFrom = inputNumberWrappers[index]?.querySelectorAll(".field__input")[0];
      const inputNumberTo = inputNumberWrappers[index]?.querySelectorAll(".field__input")[1];
      
      if (!inputRangeFrom || !inputRangeTo || !inputNumberFrom || !inputNumberTo) return;

      inputRangeFrom.oninput = () => {
        const from = parseFloat(inputRangeFrom.value) || 0;
        const to = parseFloat(inputRangeTo.value) || 0;

        if (from > to) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputNumberFrom.value = from;
        }
        this.fillSlider();
      };

      if (Number(inputRangeTo.value) <= 0) {
        inputRangeTo.style.zIndex = 2;
      } else {
        inputRangeTo.style.zIndex = 0;
      }

      inputRangeTo.oninput = () => {
        const from = parseFloat(inputRangeFrom.value) || 0;
        const to = parseFloat(inputRangeTo.value) || 0;

        if (from <= to) {
          inputRangeTo.value = to;
          inputNumberTo.value = to;
        } else {
          inputNumberTo.value = from;
          inputRangeTo.value = from;
        }

        if (Number(inputRangeTo.value) <= 0) {
          inputRangeTo.style.zIndex = 2;
        } else {
          inputRangeTo.style.zIndex = 0;
        }
        this.fillSlider();
      };
    });
  }

  controlInput() {
    const inputRangeWrappers = this.querySelectorAll(".facets__range");

    this.querySelectorAll(".facets__price-wrapper").forEach((inputWrapper, index) => {
      const inputsNumber = inputWrapper.querySelectorAll(".field__input");
      const inputNumberFrom = inputsNumber[0];
      const inputNumberTo = inputsNumber[1];
      const inputRangeFrom = inputRangeWrappers[index]?.querySelectorAll(".field__range")[0];
      const inputRangeTo = inputRangeWrappers[index]?.querySelectorAll(".field__range")[1];
      
      if (!inputRangeFrom || !inputRangeTo || !inputNumberFrom || !inputNumberTo) return;

      inputNumberFrom.oninput = () => {
        const from = parseFloat(inputNumberFrom.value) || 0;
        const to = parseFloat(inputNumberTo.value) || 0;

        if (from > to && to > 0) {
          inputRangeFrom.value = to;
          inputNumberFrom.value = to;
        } else {
          inputRangeFrom.value = from || 0;
        }
        this.fillSlider();
      };

      inputNumberTo.oninput = () => {
        const from = parseFloat(inputNumberFrom.value) || 0;
        const to = parseFloat(inputNumberTo.value) || 0;

        if (from <= to) {
          inputRangeTo.value = to;
        } else {
          inputRangeTo.value = from;
        }
        this.fillSlider();
      };
    });
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Facets Toggle and Panel Management
class FacetsToggle extends HTMLElement {
  constructor() {
    super();
    this.toggleButton = null;
    this.panel = null;
    this.overlay = null;
    this.closeButtons = [];
    this.applyButton = null;
    this.focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.toggleButton = document.getElementById(`FacetsToggle-${sectionId}`);
    this.panel = document.getElementById(`FacetsPanel-${sectionId}`);
    this.overlay = document.getElementById(`FacetsOverlay-${sectionId}`);
    this.closeButtons = this.panel?.querySelectorAll('[data-facets-close]') || [];
    this.applyButton = this.panel?.querySelector('[data-facets-apply]');

    if (this.toggleButton && this.panel) {
      this.toggleButton.addEventListener('click', this.togglePanel.bind(this));
    }

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.closePanel.bind(this));
    });

    if (this.applyButton) {
      this.applyButton.addEventListener('click', this.applyFilters.bind(this));
    }

    // Overlay click handler for drawer
    if (this.overlay) {
      this.overlay.addEventListener('click', this.closePanel.bind(this));
    }

    // Close on outside click for drawer
    if (this.panel?.classList.contains('facets-container--drawer')) {
      document.addEventListener('click', this.boundHandleOutsideClick);
    }

    // Close on Escape + trap focus on Tab
    document.addEventListener('keydown', this.boundHandleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundHandleOutsideClick);
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }

  togglePanel(event) {
    event?.stopPropagation();
    if (!this.panel) return;

    const isOpen = !this.panel.classList.contains('hidden');
    
    if (isOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  openPanel() {
    if (!this.panel || !this.toggleButton) return;
    
    this.panel.classList.remove('hidden');
    this.toggleButton.setAttribute('aria-expanded', 'true');
    
    // Show overlay for drawer
    if (this.overlay) {
      this.overlay.classList.remove('hidden');
      this.overlay.setAttribute('aria-hidden', 'false');
    }
    
    // Focus first focusable element in panel
    const firstFocusable = this.panel.querySelector('summary, input, button, select, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      setTimeout(() => firstFocusable.focus(), 100);
    }
  }

  closePanel(event) {
    event?.stopPropagation();
    if (!this.panel || !this.toggleButton) return;
    
    this.panel.classList.add('hidden');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    
    // Hide overlay for drawer
    if (this.overlay) {
      this.overlay.classList.add('hidden');
      this.overlay.setAttribute('aria-hidden', 'true');
    }
    
    // Return focus to toggle button
    if (this.toggleButton) {
      this.toggleButton.focus();
    }
  }

  applyFilters(event) {
    event?.preventDefault();
    const form = this.panel?.querySelector('form');
    if (!form) return;

    // Trigger form submission manually (for APPLY button)
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    const facetForm = form.closest('facet-filters-form');
    
    if (facetForm && typeof facetForm.onSubmitForm === 'function') {
      facetForm.onSubmitForm(searchParams, event);
    } else {
      // Fallback: trigger input event to use auto-submit
      const firstInput = form.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked, select');
      if (firstInput) {
        firstInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // Close panel after applying (only for drawer)
    if (this.panel?.classList.contains('facets-container--drawer')) {
      this.closePanel();
    }
  }

  handleOutsideClick(event) {
    if (!this.panel || !this.toggleButton) return;
    
    const isClickInsidePanel = this.panel.contains(event.target);
    const isClickOnToggle = this.toggleButton.contains(event.target);
    const isClickOnOverlay = this.overlay?.contains(event.target);
    
    if (!isClickInsidePanel && !isClickOnToggle && !isClickOnOverlay && !this.panel.classList.contains('hidden')) {
      this.closePanel();
    }
  }

  getFocusableElements() {
    if (!this.panel) return [];

    return Array.from(this.panel.querySelectorAll(this.focusableSelector)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.getAttribute('aria-hidden') === 'true') return false;
      if (element.getClientRects().length === 0) return false;
      return true;
    });
  }

  trapFocus(event) {
    const focusableElements = this.getFocusableElements();

    if (!focusableElements.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;
    const activeInsidePanel = this.panel.contains(activeElement);

    if (event.shiftKey) {
      if (activeElement === first || !activeInsidePanel) {
        event.preventDefault();
        last.focus();
      }
    } else if (activeElement === last || !activeInsidePanel) {
      event.preventDefault();
      first.focus();
    }
  }

  handleKeydown(event) {
    if (!this.panel || this.panel.classList.contains('hidden')) return;

    if (event.key === 'Escape') {
      this.closePanel();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }
}

if (!customElements.get('facets-toggle')) {
  customElements.define('facets-toggle', FacetsToggle);
}